import React, { useState } from "react";
import { Box, Grid } from "@mui/material";
import { styles } from "../styles";
import { DATA_REPORTS_TABLE_HEADERS } from "../../../../utils/constants";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import CustomTable from "../../../../components/Table/CustomTable";
import { STRINGS } from "../../strings";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { GREY, BASE, BORDER } from "../../../../utils/constants/colors";
import QuickLog from "./QuickLog";
import CircularLoader from "../../../../components/Loaders/CircularLoader";
import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
} from "../../../../Apis/apiWrapper";
import { dailyReportEndpoints } from "../../../../utils/constants/httpConstants";
import { useNavigate } from "react-router-dom";
import CustomModal from "../../../../components/Modals/CustomModal";
import { Button } from "../../../../components/Buttons";

const MeetingLogs = () => {
  const classes = styles();
  const navigate = useNavigate();
  const [reportLogs, setReportLogs] = useState([]);
  const [isLogLoading, setIsLogLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const fetchMeetings = async () => {
    setIsTableLoading(true);
    try {
      const endpoint = dailyReportEndpoints.retrieveMeetings;
      const response = await getRequest(navigate, endpoint);

      if (response?.data?.success || response?.data?.status) {
        setReportLogs(response.data.data.records || []);
      }
    } catch (err) {
      console.error("Error fetching meetings", err);
    } finally {
      setIsTableLoading(false);
    }
  };

  React.useEffect(() => {
    fetchMeetings();
  }, []);

  const handleSaveLog = async (logData) => {
    setIsLogLoading(true);
    try {
      const isEdit = !!editItem;
      const payload = {
        ...logData,
        "log-type": 5, // Hardcoded for meetings
      };

      const endpoint = isEdit
        ? dailyReportEndpoints.editActivity
        : dailyReportEndpoints.addActivity;

      const response = isEdit
        ? await putRequest(navigate, endpoint, payload)
        : await postRequest(navigate, endpoint, payload);

      if (response?.data?.success || response?.data?.status) {
        setEditItem(null);
        fetchMeetings();
        return true;
      }
    } catch (err) {
      console.error("Error saving meeting log", err);
      return false;
    } finally {
      setIsLogLoading(false);
    }
  };

  const handleEditLog = (item) => {
    setEditItem(item);
  };

  const handleDeleteLog = (item) => {
    setDeleteItem(item);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteLog = async () => {
    if (!deleteItem) return;
    setIsDeleteLoading(true);
    try {
      const endpoint = `${dailyReportEndpoints.deleteActivity}?id=${deleteItem._id}`;
      const response = await deleteRequest(endpoint);
      if (response?.data?.success || response?.data?.status) {
        setIsDeleteModalOpen(false);
        setDeleteItem(null);
        fetchMeetings();
      }
    } catch (err) {
      console.error("Error deleting meeting log", err);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const TableData = {
    heads: [
      { id: "date", label: "Date", ui_id: "bold_text", width: "8vw" },
      { id: "time", label: "Time", ui_id: "text", width: "6vw" },
      {
        id: "prospect_name",
        label: "Prospect Name",
        ui_id: "text",
        width: "12vw",
      },
      {
        id: "prospect_email",
        label: "Prospect Email",
        ui_id: "text",
        width: "14vw",
      },
      { id: "duration", label: "Duration", ui_id: "text", width: "8vw" },
      { id: "status", label: "Status", ui_id: "text", width: "8vw" },
      {
        id: "description",
        label: "Description",
        ui_id: "text",
        width: "20vw",
      },
      { id: "actions", label: "Actions", ui_id: "action", width: "6vw" },
    ],
    body: reportLogs,
    isLoading: isTableLoading,
    total_records: reportLogs.length,
    is_selection_not_allowed: true,
    onEdit: handleEditLog,
    onDelete: handleDeleteLog,
    operations: [
      {
        id: "edit",
        label: "Edit",
        iconLabel: ICON_LABELS.EDIT,
        function: handleEditLog,
      },
      {
        id: "delete",
        label: "Delete",
        iconLabel: ICON_LABELS.DELETE,
        function: handleDeleteLog,
      },
    ],
  };

  return (
    <Box
      className={classes.container}
      style={{
        margin: "16px",
        height: "calc(100vh - 7rem)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        className={classes.componentContainer}
        style={{
          background: "white",
          borderRadius: "0px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "none",
          border: `1px solid ${BORDER.DEFAULT}`,
          margin: 0,
        }}
      >
        <Box
          className={classes.flexBetween}
          style={{
            padding: "20px 24px",
            borderBottom: `2px solid ${BORDER.DEFAULT}`,
            backgroundColor: "#fff",
          }}
        >
          <Box
            className={classes.padUs}
            style={{ paddingTop: 0, paddingLeft: 0 }}
          >
            <TypographyInline
              size={"lg"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.BOLD}
              label="Meeting Logs"
            />
          </Box>
        </Box>

        <Box
          style={{
            flex: 1,
            padding: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid
            container
            spacing={0}
            style={{
              flex: 1,
              border: `1px solid ${BORDER.DEFAULT}`,
              height: "100%",
            }}
          >
            <Grid
              item
              xs={10}
              style={{
                height: "100%",
                borderRight: `1px solid ${BORDER.DEFAULT}`,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                style={{
                  flex: 1,
                  backgroundColor: BASE.WHITE,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CustomTable tableData={TableData} />
              </Box>
            </Grid>
            <Grid
              item
              xs={2}
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                style={{
                  flex: 1,
                  backgroundColor: "#f8f9fa",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <QuickLog
                  activeTab="meetings"
                  onSave={handleSaveLog}
                  isLoading={isLogLoading}
                  editData={editItem}
                  onCancel={() => setEditItem(null)}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <CustomModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        header={{ heading: { label: "Confirm Deletion" } }}
        width="400px"
      >
        <Box>
          <TypographyInline
            size="md"
            label="Are you sure you want to delete this meeting log? This action cannot be undone."
            color={GREY.PRIMARY}
          />
          <Box style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
            <Button
              label="Cancel"
              variant="ghost"
              size="md40"
              fullWidth
              onClick={() => setIsDeleteModalOpen(false)}
            />
            <Button
              label="Delete"
              variant="primary"
              size="md40"
              fullWidth
              onClick={confirmDeleteLog}
              isLoading={isDeleteLoading}
            />
          </Box>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default MeetingLogs;
